// Conditional persistent grant reconciler. Pure. No React, no
// broadcasts. GameBoard collects the wanted set during
// recomputeStaticAuras, hands it here for the diff against the
// existing _conditional-flagged ledger entries, and applies the
// returned {toAdd, toRemove} ops with sync calls.
//
// Used by The Empyrean and other "Provides X if you control nearby Y"
// patterns. The diff is keyed by (sourceCardId, controller, op, args)
// so multiple independent conditional grants from the same source can
// coexist. Including the Abundance pattern where one aura grants
// mana to BOTH players (each covered site provides +1 mana to its
// own controller, not to the aura's controller).

const wantedKey = (sourceId, controller, op, args) => `${sourceId}|${controller}|${op}|${JSON.stringify(args)}`;

/**
 * @typedef {Object} WantedGrant
 * @property {Object} sourceCard
 * @property {'p1'|'p2'} controller
 * @property {Object} effect - { op, args }
 * @property {Object} entry
 */

/**
 * @typedef {Object} ConditionalDiff
 * @property {Array<Object>} toAdd     - ledger entries to insert (with _conditional: true)
 * @property {Array<Object>} toRemove  - existing ledger entries to drop
 * @property {boolean} mutated         - true if either list is non-empty
 */

/**
 * Diff the wanted set against the existing ledger.
 *
 * @param {WantedGrant[]} wanted - this frame's active conditional grants
 * @param {Array<Object>} ledgerEntries - current effectLedger.entries
 * @returns {ConditionalDiff}
 */
export function diffConditionalGrants(wanted, ledgerEntries) {
  const wantedKeys = new Set(
    wanted.map((w) => wantedKey(w.sourceCard.id, w.controller, w.effect.op, w.effect.args)),
  );

  const toRemove = ledgerEntries.filter(
    (e) => e._conditional
      && !wantedKeys.has(wantedKey(e.sourceCardId, e.controller, e.effect.op, e.effect.args)),
  );

  const toAdd = [];
  for (const w of wanted) {
    const key = wantedKey(w.sourceCard.id, w.controller, w.effect.op, w.effect.args);
    const exists = ledgerEntries.some(
      (e) => e._conditional && wantedKey(e.sourceCardId, e.controller, e.effect.op, e.effect.args) === key,
    );
    if (exists) continue;
    toAdd.push({
      sourceCardId: w.sourceCard.id,
      sourceCardName: w.sourceCard.name,
      controller: w.controller,
      effect: w.effect,
      expiry: { type: 'permanent' },
      _conditional: true,
    });
  }

  return { toAdd, toRemove, mutated: toAdd.length > 0 || toRemove.length > 0 };
}
